import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import type { WidgetError, WidgetFieldError } from '../types'

// ─── Context ───────────────────────────────────────────────────────────────────

interface FormErrorContextValue {
  /** Current error map: field name → human-readable detail. */
  errors: Record<string, string>
  /** Generate a stable DOM id for the error message of a given field. */
  errorId(name: string): string
}

const FormErrorContext = createContext<FormErrorContextValue | null>(null)

// ─── Provider ──────────────────────────────────────────────────────────────────

/**
 * Structural shape the provider reads off the `useMutation` return. Mirroring these fields
 * instead of importing the full `UseMutationResult<…>` generic lets callers pass any mutation
 * regardless of its `TData` / `TVariables` / `TContext` generic parameters — zero casting.
 */
export interface FormErrorMutationSource {
  error: unknown
  status?: string
  submittedAt?: number
}

export interface FormErrorProviderProps {
  /**
   * The useMutation result whose thrown `WidgetError.errors` feed per-field server errors.
   * Accepts the raw `useMutation` return; the provider reads `.error`, `.status`, and `.submittedAt`
   * so stale errors auto-clear when the mutation transitions to `pending` or `success`.
   */
  mutation?: FormErrorMutationSource
  /**
   * Optional client-side validation errors. Take precedence over server errors for the same field
   * so client-detected issues (e.g. "title must be ≥ 5 chars") surface immediately without a round-
   * trip. Caller owns population / clearing.
   */
  clientErrors?: Record<string, string>
  children: ReactNode
}

export function FormErrorProvider({ mutation, clientErrors, children }: FormErrorProviderProps) {
  const idPrefix = useId()

  const serverErrors = useServerErrors(mutation)

  const errors = useMemo<Record<string, string>>(() => {
    const merged: Record<string, string> = { ...serverErrors }
    if (clientErrors) {
      for (const [field, detail] of Object.entries(clientErrors)) {
        if (detail) merged[field] = detail
      }
    }
    return merged
  }, [serverErrors, clientErrors])

  const errorId = useCallback((name: string) => `${idPrefix}-err-${name}`, [idPrefix])

  const value = useMemo<FormErrorContextValue>(() => ({ errors, errorId }), [errors, errorId])

  return (
    <FormErrorContext.Provider value={value}>
      {children}
    </FormErrorContext.Provider>
  )
}

/**
 * Extract a per-field error map from a `useMutation`'s current error object. Clears automatically
 * as soon as the mutation leaves the `'error'` state (i.e. on retry or success) so the user never
 * sees stale "priority invalid" red after they've fixed it and re-submitted.
 */
function useServerErrors(
  mutation: FormErrorMutationSource | undefined,
): Record<string, string> {
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})
  const lastSeenAttemptRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!mutation) { setServerErrors({}); return }

    // Clear when the mutation transitions away from 'error' (pending / success / idle).
    if (mutation.status !== 'error') {
      if (Object.keys(serverErrors).length > 0) setServerErrors({})
      return
    }

    // Only rebuild the map when we see a *new* error attempt, otherwise we'd clear-and-reset on
    // every render. `submittedAt` ticks forward on each mutate() call so it's a reliable guard.
    if (lastSeenAttemptRef.current === mutation.submittedAt) return
    lastSeenAttemptRef.current = mutation.submittedAt

    const err = mutation.error as WidgetError | null | undefined
    if (!err || !err.errors || err.errors.length === 0) { setServerErrors({}); return }

    const next: Record<string, string> = {}
    for (const fe of err.errors as WidgetFieldError[]) {
      const key = fe.field
      if (!key) continue
      // Only the first error per field wins — matches JSON:API's "one pointer per field" norm
      // and keeps the inline message from thrashing if the server returns duplicates.
      if (!(key in next)) next[key] = fe.detail || fe.code
    }
    setServerErrors(next)
  }, [mutation, mutation?.status, mutation?.submittedAt, mutation?.error, serverErrors])

  return serverErrors
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export interface UseFieldErrorReturn {
  error: string | null
  invalid: boolean
  inputProps: {
    'aria-invalid'?: 'true'
    'aria-describedby'?: string
    className?: string
  }
  /** Stable id for the error-message element. Use it to wire `aria-describedby` manually if you
   *  aren't spreading `inputProps` onto a native input. */
  errorId: string
}

/**
 * Reads the current error for a named field. Returns the detail + pre-computed a11y props so the
 * caller can spread them onto any native input/select/textarea with zero branching.
 */
export function useFieldError(name: string): UseFieldErrorReturn {
  const ctx = useContext(FormErrorContext)
  const error = ctx?.errors[name] ?? null
  const invalid = error !== null
  const id = ctx?.errorId(name) ?? `rqd-err-${name}`

  return {
    error,
    invalid,
    inputProps: invalid
      ? {
        'aria-invalid': 'true',
        'aria-describedby': id,
        className: 'rqd-field-error',
      }
      : {},
    errorId: id,
  }
}

// ─── <FieldError> ──────────────────────────────────────────────────────────────

export interface FieldErrorProps {
  name: string
  as?: 'div' | 'span' | 'p'
  className?: string
}

/**
 * Renders the per-field error message under an input. Uses `role="alert"` so screen readers
 * announce it when it appears (e.g. after a failed submission). Renders nothing when there's no
 * error — safe to leave in the markup unconditionally.
 */
export function FieldError({ name, as = 'div', className }: FieldErrorProps) {
  const ctx = useContext(FormErrorContext)
  const detail = ctx?.errors[name]
  if (!detail) return null

  const id = ctx!.errorId(name)
  const cls = className ? `rqd-error-text ${className}` : 'rqd-error-text'
  const commonProps = { id, role: 'alert' as const, className: cls }

  switch (as) {
    case 'span': return <span {...commonProps}>{detail}</span>
    case 'p': return <p {...commonProps}>{detail}</p>
    default: return <div {...commonProps}>{detail}</div>
  }
}

// ─── <FormFieldGroup> ──────────────────────────────────────────────────────────

export interface FormFieldGroupProps {
  /** Field name — must match the `name` attribute on the wrapped input. */
  name: string
  label?: ReactNode
  hint?: ReactNode
  className?: string
  children: ReactNode
}

/**
 * Common label + input + `<FieldError>` layout for a form field inside a `<FormErrorProvider>`.
 * The child input MUST carry a `name` attribute matching `props.name`; the group injects the
 * `aria-invalid` / `aria-describedby` / error-class props by cloning the child.
 *
 * For layouts that don't fit this shape (e.g. file drop zones, category tree, diagnostic checkbox
 * blocks), skip this component and call `useFieldError(name)` directly.
 */
export function FormFieldGroup({ name, label, hint, className, children }: FormFieldGroupProps) {
  const { inputProps } = useFieldError(name)

  const rootCls = className ? `rqd-form-group ${className}` : 'rqd-form-group'
  const merged = isValidElement(children) ? cloneWithMergedProps(children, inputProps) : children

  return (
    <div className={rootCls}>
      {label && <label className="rqd-label">{label}</label>}
      {merged}
      {hint && <div className="rqd-field-hint">{hint}</div>}
      <FieldError name={name} />
    </div>
  )
}

function cloneWithMergedProps(
  child: ReactElement,
  extra: UseFieldErrorReturn['inputProps'],
): ReactElement {
  const existing = (child.props ?? {}) as Record<string, unknown>
  const existingClass = typeof existing.className === 'string' ? existing.className : ''
  const mergedClass = [existingClass, extra.className].filter(Boolean).join(' ').trim() || undefined

  const mergedProps = {
    ...existing,
    'aria-invalid': extra['aria-invalid'] ?? existing['aria-invalid'],
    'aria-describedby': extra['aria-describedby'] ?? existing['aria-describedby'],
    className: mergedClass,
  } as Record<string, unknown>
  return cloneElement(child, mergedProps)
}
